import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Paper, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import omnichannelInboxApi from "../../services/omnichannelInboxApi";
import MessageAttachmentGallery from "./MessageAttachmentGallery";

const COLORS = {
  inboundBg: "#FFFFFF",
  outboundBg: "#D9FDD3",
  noteBg: "#FFF3C8",
  systemBg: "#E9EFF4",
  text: "#111B21",
  subtext: "#667781",
  border: "rgba(17,27,33,0.06)",
};

const INBOX_TIME_ZONE = "Africa/Harare";
const WHATSAPP_READ_BLUE = "#53BDEB";

const formatTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: INBOX_TIME_ZONE,
  }).format(date);
};

const formatDayLabel = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: INBOX_TIME_ZONE,
  }).format(date);
};

const toTimestamp = (message) => {
  const raw = message?.occurred_at || message?.created_at;
  if (!raw) return 0;

  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const mediaFallbackText = (message) => {
  const metadata = message?.metadata || {};
  const providerMessage = metadata?.provider_payload?.message || {};
  const contentType = String(metadata?.content_type || providerMessage?.type || "").toLowerCase();

  switch (contentType) {
    case "audio":
      return providerMessage?.audio?.voice ? "Voice note" : "Audio message";
    case "document":
      return providerMessage?.document?.filename ? `Document: ${providerMessage.document.filename}` : "Document";
    case "image":
      return "Image";
    case "video":
      return "Video";
    case "sticker":
      return "Sticker";
    case "location":
      return providerMessage?.location?.name
        ? `Location shared: ${providerMessage.location.name}`
        : providerMessage?.location?.address
          ? `Location shared: ${providerMessage.location.address}`
          : "Location shared";
    case "contacts": {
      const count = Array.isArray(providerMessage?.contacts) ? providerMessage.contacts.length : 0;
      return count > 1 ? `${count} contacts shared` : "Contact shared";
    }
    case "reaction":
      return providerMessage?.reaction?.emoji ? `Reacted with ${providerMessage.reaction.emoji}` : "Reaction received";
    default:
      return metadata?.display_text || null;
  }
};

const getDisplayText = (message) => {
  const text = typeof message?.body?.text === "string" ? message.body.text.trim() : "";
  if (text) {
    return text;
  }

  if (Array.isArray(message?.attachments) && message.attachments.length) {
    return "";
  }

  return mediaFallbackText(message) || "No message body";
};

const formatAudioTime = (value) => {
  if (!Number.isFinite(value) || value < 0) {
    return "0:00";
  }

  const wholeSeconds = Math.floor(value);
  const minutes = Math.floor(wholeSeconds / 60);
  const seconds = wholeSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const normalizeDeliveryState = (message) => {
  const deliveryStatus = String(message?.delivery_status || "").trim().toLowerCase();
  const status = String(message?.status || "").trim().toLowerCase();

  if (["read", "played"].includes(deliveryStatus)) {
    return "read";
  }

  if (deliveryStatus === "delivered") {
    return "delivered";
  }

  if (["sent", "pending", "queued", "accepted"].includes(deliveryStatus)) {
    return "sent";
  }

  if (["read", "played"].includes(status)) {
    return "read";
  }

  if (status === "delivered") {
    return "delivered";
  }

  return "sent";
};

function WhatsAppDeliveryStatusIcon({ state = "sent" }) {
  const color = state === "read" ? WHATSAPP_READ_BLUE : COLORS.subtext;
  const strokeWidth = 1.8;

  if (state === "delivered" || state === "read") {
    return (
      <Box
        aria-label={state === "read" ? "Read" : "Delivered"}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 15,
          height: 11,
          color,
          ml: 0.15,
        }}
      >
        <svg width="15" height="11" viewBox="0 0 15 11" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M1.6 5.8L4.2 8.45L8.5 2.95"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.2 5.8L8.8 8.45L13.1 2.95"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
    );
  }

  return (
    <Box
      aria-label="Sent"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 12,
        height: 11,
        color,
        ml: 0.1,
      }}
    >
      <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M1.5 5.85L4 8.35L8.3 3.05"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
}

const WAVE_BARS = [10, 15, 12, 18, 14, 11, 19, 13, 17, 12, 9, 16, 11, 15, 10, 18, 12, 14];

function WhatsAppAudioPlayer({ message }) {
  const [audioUrl, setAudioUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;

    return () => {
      if (audio) {
        audio.pause();
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return undefined;
    }

    const handleLoadedMetadata = () => setDuration(audio.duration || 0);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      audio.currentTime = 0;
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [audioUrl]);

  const handleTogglePlayback = async () => {
    if (loading || !message?.conversation_id || !message?.id) {
      return;
    }

    if (audioRef.current && audioUrl) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        await audioRef.current.play();
      }
      return;
    }

    setLoading(true);
    setError("");

    try {
      const blob = await omnichannelInboxApi.fetchConversationMessageMedia(message.conversation_id, message.id);
      const nextUrl = URL.createObjectURL(blob);

      setAudioUrl((previousUrl) => {
        if (previousUrl) {
          URL.revokeObjectURL(previousUrl);
        }

        return nextUrl;
      });

      window.setTimeout(() => {
        audioRef.current?.play().catch(() => {
          setIsPlaying(false);
        });
      }, 0);
    } catch (loadError) {
      setError(loadError?.response?.data?.message || loadError?.message || "Unable to load the voice note.");
    } finally {
      setLoading(false);
    }
  };

  const progressRatio = duration > 0 ? Math.min(currentTime / duration, 1) : 0;
  const activeBars = Math.max(1, Math.round(progressRatio * WAVE_BARS.length));

  return (
    <Box
      sx={{
        mt: 0.85,
        minWidth: { xs: 240, sm: 300 },
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="center">
        <IconButton
          onClick={handleTogglePlayback}
          disabled={loading}
          sx={{
            width: 42,
            height: 42,
            flexShrink: 0,
            bgcolor: "#25D366",
            color: "#FFFFFF",
            "&:hover": {
              bgcolor: "#1fb55a",
            },
            "&.Mui-disabled": {
              bgcolor: alpha("#25D366", 0.38),
              color: "#FFFFFF",
            },
          }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: "#FFFFFF" }} /> : isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
        </IconButton>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.35} alignItems="center" sx={{ height: 28 }}>
            {WAVE_BARS.map((height, index) => (
              <Box
                key={`${message?.id || "audio"}-${index}`}
                sx={{
                  width: 4,
                  height,
                  borderRadius: 999,
                  bgcolor: index < activeBars ? "#25D366" : alpha("#6C7A89", 0.35),
                  transition: "background-color 120ms ease",
                }}
              />
            ))}
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.35 }}>
            <Typography variant="caption" sx={{ color: COLORS.subtext, fontWeight: 600 }}>
              {message?.media?.is_voice_note ? "Voice note" : "Audio message"}
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.subtext, fontWeight: 700 }}>
              {formatAudioTime(isPlaying || currentTime > 0 ? currentTime : duration)}
            </Typography>
          </Stack>
        </Box>
      </Stack>

      <audio ref={audioRef} preload="metadata" src={audioUrl || undefined} style={{ display: "none" }}>
        Your browser does not support audio playback.
      </audio>

      {error ? (
        <Typography variant="caption" sx={{ display: "block", mt: 0.55, color: "#B94A48" }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function WhatsAppThreadView({ messages = [] }) {
  const bottomRef = useRef(null);

  const orderedMessages = useMemo(() => {
    return [...messages].sort((left, right) => {
      const timeDelta = toTimestamp(left) - toTimestamp(right);
      if (timeDelta !== 0) {
        return timeDelta;
      }

      return (left?.id || 0) - (right?.id || 0);
    });
  }, [messages]);

  const latestMessageSignature = useMemo(() => {
    const latestMessage = orderedMessages[orderedMessages.length - 1];
    if (!latestMessage) {
      return "empty";
    }

    return JSON.stringify({
      id: latestMessage?.id || null,
      status: latestMessage?.status || null,
      occurred_at: latestMessage?.occurred_at || latestMessage?.created_at || null,
      body_text: getDisplayText(latestMessage),
    });
  }, [orderedMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [latestMessageSignature]);

  if (!orderedMessages.length) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No messages have been captured for this conversation yet.
        </Typography>
      </Box>
    );
  }

  let previousDay = "";

  return (
    <Box sx={{ px: { xs: 1.2, md: 2.5 }, py: { xs: 1, md: 1.6 } }}>
      <Stack spacing={0.7}>
        {orderedMessages.map((message) => {
          const occurredAt = message?.occurred_at || message?.created_at;
          const dayLabel = formatDayLabel(occurredAt);
          const showDayLabel = dayLabel && dayLabel !== previousDay;
          previousDay = dayLabel || previousDay;

          const inbound = message?.direction === "inbound";
          const system = message?.message_type === "system";
          const internalNote = message?.message_type === "internal_note";
          const isAudioMessage = Boolean(message?.media?.available && message?.media?.content_type === "audio");
          const messageAttachments = Array.isArray(message?.attachments)
            ? message.attachments.filter((attachment) => !(isAudioMessage && attachment?.source === "whatsapp_media"))
            : [];
          const align = system ? "center" : inbound ? "flex-start" : "flex-end";
          const bubbleBg = system
            ? COLORS.systemBg
            : internalNote
              ? COLORS.noteBg
              : inbound
                ? COLORS.inboundBg
                : COLORS.outboundBg;
          const deliveryState = !inbound && !system && !internalNote
            ? normalizeDeliveryState(message)
            : null;

          const label = system ? "System update" : internalNote ? "Internal note" : "";
          const bubbleMaxWidth = isAudioMessage ? { xs: "95%", md: 420 } : { xs: "88%", md: "72%" };
          const bubblePaddingX = isAudioMessage ? 1.35 : 1.2;
          const bubblePaddingY = isAudioMessage ? 1.05 : 0.85;
          const showMessageHeader = system || internalNote;

          return (
            <React.Fragment key={message.id}>
              {showDayLabel ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 0.45 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.2,
                      py: 0.45,
                      borderRadius: 999,
                      bgcolor: alpha("#FFFFFF", 0.9),
                      color: COLORS.subtext,
                      border: `1px solid ${alpha("#D4D9DD", 0.9)}`,
                      boxShadow: "0 1px 1px rgba(17,27,33,0.08)",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {dayLabel}
                    </Typography>
                  </Paper>
                </Box>
              ) : null}

              <Box sx={{ display: "flex", justifyContent: align }}>
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: bubbleMaxWidth,
                    width: isAudioMessage ? "100%" : "auto",
                    px: bubblePaddingX,
                    py: bubblePaddingY,
                    borderRadius: system ? "14px" : inbound ? "10px 10px 10px 3px" : "10px 10px 3px 10px",
                    bgcolor: bubbleBg,
                    color: COLORS.text,
                    border: showMessageHeader ? `1px solid ${COLORS.border}` : "none",
                    boxShadow: "0 1px 1px rgba(17,27,33,0.08)",
                  }}
                >
                  {showMessageHeader ? (
                    <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between">
                      <Typography
                        variant="caption"
                        sx={{
                          color: internalNote ? "#8A6D1B" : COLORS.subtext,
                          fontWeight: 700,
                          pr: 1,
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.subtext, flexShrink: 0 }}>
                        {formatTimestamp(occurredAt)}
                      </Typography>
                    </Stack>
                  ) : null}

                  {isAudioMessage ? (
                    <WhatsAppAudioPlayer message={message} />
                  ) : (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: showMessageHeader ? 0.3 : 0,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          lineHeight: 1.5,
                        }}
                      >
                        {getDisplayText(message)}
                      </Typography>
                      <MessageAttachmentGallery attachments={messageAttachments} compact />
                    </>
                  )}

                  {isAudioMessage && messageAttachments.length ? (
                    <MessageAttachmentGallery attachments={messageAttachments} compact />
                  ) : null}

                  {!showMessageHeader ? (
                    <Stack
                      direction="row"
                      spacing={0.45}
                      justifyContent="flex-end"
                      alignItems="center"
                      sx={{ mt: 0.45 }}
                    >
                      <Typography variant="caption" sx={{ color: COLORS.subtext, lineHeight: 1 }}>
                        {formatTimestamp(occurredAt)}
                      </Typography>
                      {!inbound && deliveryState ? (
                        <WhatsAppDeliveryStatusIcon state={deliveryState} />
                      ) : null}
                    </Stack>
                  ) : null}
                </Paper>
              </Box>
            </React.Fragment>
          );
        })}
        <Box ref={bottomRef} sx={{ height: 1 }} />
      </Stack>
    </Box>
  );
}
